import { type ReactElement, type ReactNode, useMemo, useState } from 'react'
import { cn } from '@util'
import { Checkbox, Combobox, Group, Text } from '@mantine/core'
import { useTranslation } from '@front/model'
import {
  useMultiSelectFilterToggleLabels,
  type MultiSelectFilterToggleLabels,
} from './useMultiSelectFilterToggleLabels'
import { type MultiSelectFilterRowAction } from './multiSelectFilterValue'

type Props = {
  label: string
  value: string
  isSelected: boolean
  doesToggleSelectAll: boolean
  disabled?: boolean
  leftSection?: ReactNode
  onToggle: VoidFunction
  rowAction: MultiSelectFilterRowAction
  indented?: boolean
  highlighted?: boolean
}

type MultiSelectFilterLabels = MultiSelectFilterToggleLabels & {
  rowActionLabel: string
  rowAriaLabel: string
}

export const useMultiSelectFilterLabels = ({
  label,
  isSelected,
  doesToggleSelectAll,
  rowAction,
}: Pick<Props, 'label' | 'isSelected' | 'doesToggleSelectAll' | 'rowAction'>): MultiSelectFilterLabels => {
  const { t } = useTranslation()
  const { toggleLabel, toggleAriaLabel } = useMultiSelectFilterToggleLabels({ label, isSelected, doesToggleSelectAll })

  return useMemo(() => {
    const rowActionLabel = {
      check: t('Check'),
      selectAll: t('Check all'),
      selectOnly: t('Only'),
    }[rowAction]
    const rowAriaLabel = {
      check: t('Check {{label}}', { label }),
      selectAll: t('Check all by selecting {{label}}', { label }),
      selectOnly: t('Select only {{label}}', { label }),
    }[rowAction]

    return { toggleLabel, toggleAriaLabel, rowActionLabel, rowAriaLabel }
  }, [label, rowAction, t, toggleAriaLabel, toggleLabel])
}

export const MultiSelectFilterOption = ({
  label,
  value,
  isSelected,
  doesToggleSelectAll,
  disabled,
  leftSection,
  onToggle,
  rowAction,
  indented = false,
  highlighted = false,
}: Props): ReactElement => {
  const [isToggleActive, setIsToggleActive] = useState(false)
  const { toggleLabel, toggleAriaLabel, rowActionLabel, rowAriaLabel } = useMultiSelectFilterLabels({
    label,
    isSelected,
    doesToggleSelectAll,
    rowAction,
  })

  return (
    <div
      className={cn(
        'group hover:bg-default-hover focus-within:bg-default-hover flex items-center rounded-sm',
        indented && 'pl-3xl',
        highlighted && 'bg-default-hover'
      )}
    >
      <div
        className='px-md flex shrink-0 items-center self-stretch'
        onMouseEnter={() => setIsToggleActive(true)}
        onMouseLeave={() => setIsToggleActive(false)}
        onFocus={() => setIsToggleActive(true)}
        onBlur={() => setIsToggleActive(false)}
      >
        <Checkbox
          checked={isSelected}
          disabled={disabled}
          aria-label={toggleAriaLabel}
          onChange={onToggle}
          className='cursor-pointer'
        />
      </div>
      <Combobox.Option
        value={value}
        disabled={disabled}
        active={highlighted || undefined}
        aria-selected={highlighted || undefined}
        aria-label={rowAriaLabel}
        className='min-w-0 flex-1'
      >
        <Group justify='space-between' wrap='nowrap'>
          <Group flex={1} wrap='nowrap' className='min-w-0'>
            {leftSection}
            <Text size='sm' lineClamp={2} title={label} className='min-w-0 flex-1'>
              {label}
            </Text>
          </Group>
          <Text
            size='sm'
            c='dimmed'
            aria-hidden
            className='w-20 shrink-0 text-right whitespace-nowrap opacity-0 transition-opacity group-focus-within:opacity-100 group-hover:opacity-100'
          >
            {isToggleActive ? toggleLabel : rowActionLabel}
          </Text>
        </Group>
      </Combobox.Option>
    </div>
  )
}
